import { uniqueId } from 'lodash';
import React from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import ButtonKit from '@components/common/ButtonKit';
import LogicDivider from '@components/common/LogicDivider';
import LogicSelector from '@components/common/LogicSelector';
import Label from '@components/common/NumberLabel';
import {
  AddRuleButtonWrapper,
  CusSelect,
  Prefix,
  RuleGroupHeader,
  RuleGroupHeaderLeft,
  RuleGroupWrapper,
} from '@components/data-transform-panel/components/TransformRuleGroup/styled';
import { ITransformRuleGroup } from '@components/data-transform-panel/components/TransformRuleGroup/typed';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import {
  EInputType,
  positiveOperatorOptions,
} from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder/typed';
import MetadataRuleRow from '@components/mapping-panel/components/RuleBuilder/MetadataRuleRow';
import { RuleList } from '@components/mapping-panel/components/RuleBuilder/RuleGroup/styled';
import { Icon } from '@components/metrics-builder/components/MetricList';
import AddRuleIconSvg from '@icons/mapping-panel/add-rule-icon.svg';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { usePresetList } from '@src/api/emp/queryHooks';

const TransformRuleGroup: React.FC<ITransformRuleGroup> = ({
  activeIndices,
  data,
  orIndex = 0,
  updateActiveIndices,
  onChange,
  onDeleteTransformMetadataGroup,
  onDeleteTransformMetadataItem,
  onAddAndTransformMetadataItem,
  onAddOrTransformMetadataItem,
  onLogicChange,
  onDragEnd,
  metadataList = [],
  metaNameError,
  ruleError,
}) => {
  const { isLoading } = usePresetList();
  const { data: dimensionData, isFetching: isDimensionDataLoading } = useQueryDimensionData(
    {
      fields: data?.source_dimension ? [data?.source_dimension] : [],
      filters: [],
    },
    500,
  );

  const ruleGroupLabel = orIndex + 1;
  const sourceDimension = data?.source_dimension;
  const dimensionDisplayName = metadataList.find((item) => item.value === sourceDimension)?.label;

  const hasLogicSelector = orIndex !== 0;
  const isActive = activeIndices?.orIndex === orIndex;

  const items: JSX.Element[] = [];
  let count = 0;

  data?.rule.forEach((subAndGroup, subAndIndex) => {
    if (subAndIndex !== 0) {
      items.push(
        <LogicDivider
          key={uniqueId('Transform_LogicDivider_')}
          value="AND"
          onChange={() => onLogicChange?.(subAndIndex)}
          onDelete={() => onLogicChange?.(subAndIndex)}
        />,
      );
    }

    const subAndGroupRules = subAndGroup.map((subOrRuleItem, subOrIndex) => {
      count++;
      const rowLabel = `${orIndex + 1}.${count}`;
      const hasBorderBottom = subAndGroup?.length - 1 !== subOrIndex;
      const itemActiveIndices = activeIndices?.ruleItems?.find(
        (i) => i.subAndIndex === subAndIndex && i.subOrIndex === subOrIndex,
      );

      return (
        <MetadataRuleRow
          isNoRules={ruleError}
          id={`rename_metadata_rule_${rowLabel}`}
          key={rowLabel}
          rowLabel={rowLabel}
          index={subOrIndex}
          itemActiveIndices={isActive ? itemActiveIndices : undefined}
          showLogicOperator={subOrIndex !== 0}
          dimensionName={dimensionDisplayName}
          data={{ ...subOrRuleItem, key: sourceDimension ?? '' }}
          options={data?.source_dimension ? dimensionData : []}
          loading={isDimensionDataLoading}
          style={hasBorderBottom ? undefined : { borderBottom: 'none' }}
          onActive={() => updateActiveIndices?.(false, [{ subAndIndex, subOrIndex }])}
          onDelete={() => {
            onDeleteTransformMetadataItem?.(subAndIndex, subOrIndex);
            updateActiveIndices?.(false, []);
          }}
          onChange={(...rest) => onChange?.(subAndIndex, subOrIndex, ...rest)}
          onLogicChange={() => onLogicChange?.(subAndIndex, subOrIndex)}
          operatorOptions={positiveOperatorOptions}
        />
      );
    });

    const droppableId = JSON.stringify({ orIndex, subAndIndex });
    const droppableArea = (
      <Droppable droppableId={droppableId} key={droppableId}>
        {(provided, snapshot) => (
          <RuleList
            ref={provided.innerRef}
            {...provided.droppableProps}
            isDraggingOver={snapshot.isDraggingOver}
          >
            {subAndGroupRules}
            {provided.placeholder}
          </RuleList>
        )}
      </Droppable>
    );
    items.push(droppableArea);
  });

  const handleMetadataNameChange = (value) => {
    onChange?.(-1, -1, EInputType.METADATA_NAME, value);
  };

  const handelDragEnd = (...args) => {
    onDragEnd?.(args[0], args[1]);
  };

  return (
    <DragDropContext onDragEnd={handelDragEnd}>
      <RuleGroupWrapper onClick={() => updateActiveIndices?.(false, [])}>
        <RuleGroupHeader active={isActive}>
          <RuleGroupHeaderLeft>
            <Prefix type="group">
              {hasLogicSelector && <LogicSelector value="AND" />}
              <Label value={ruleGroupLabel} square />
            </Prefix>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
              }}
            >
              <span style={{ letterSpacing: '-0.01em', color: 'rgba(0, 0, 0, 0.65)' }}>
                Metadata Name *
              </span>
              <span style={{ padding: '0 8px 0 6px' }}>=</span>
              <CusSelect
                status={metaNameError ? 'error' : undefined}
                data-test-id={`rename_metadata_name_${ruleGroupLabel}`}
                loading={isLoading}
                active={isActive && activeIndices?.metadataNameActive}
                value={sourceDimension}
                placeholder="Please select..."
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                onChange={handleMetadataNameChange}
                onClick={(e) => e?.stopPropagation()}
                onFocus={(e) => {
                  e?.stopPropagation();
                  updateActiveIndices?.(true, []);
                }}
                onBlur={() => updateActiveIndices?.(false, [])}
                options={metadataList}
                dropdownStyle={{
                  minWidth: 'fit-content',
                }}
              />
            </div>
          </RuleGroupHeaderLeft>
          <Icon
            style={{ marginRight: '8px' }}
            component={DeleteSvg}
            activeComponent={DeleteActiveSvg}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteTransformMetadataGroup?.();
            }}
          />
        </RuleGroupHeader>
        <div style={{ padding: '0 12px 0 76px' }}>
          {items}
          {isActive && (
            <AddRuleButtonWrapper>
              <ButtonKit
                kitType="primary"
                borderColor="transparent"
                style={{ height: '24px', padding: '2px 6px', fontWeight: 400 }}
                prefix={<Icon component={AddRuleIconSvg} />}
                onClick={() => onAddOrTransformMetadataItem?.()}
              >
                Add OR Rule
              </ButtonKit>
              <ButtonKit
                kitType="primary"
                borderColor="transparent"
                style={{ height: '24px', padding: '2px 6px', fontWeight: 400 }}
                prefix={<Icon component={AddRuleIconSvg} />}
                onClick={() => onAddAndTransformMetadataItem?.()}
              >
                Add AND Rule
              </ButtonKit>
            </AddRuleButtonWrapper>
          )}
        </div>
      </RuleGroupWrapper>
    </DragDropContext>
  );
};

export default TransformRuleGroup;
